import {DATA_TYPES, HEADER_NAME} from '../../constants/constants'
import {
    CompletionCallMetadata,
    getNextIdForPrefix,
    getSectionNameByPointer,
    ignoredTemplateOutlineTypes,
    invalidOutlineRetryAmount,
    type PointerPredicate,
    reportBIError,
    unsectionedOutlineSectionName
} from './aiExtensionContent'
import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {Pointer, IdMap} from '@wix/document-services-types'
import type {StructureExtensionAPI} from '../structure'
import type {ComponentDefinitionExtensionAPI} from '../componentDefinition'
import {getReportableFromError, ReportableError} from '@wix/document-manager-utils'
import {extractTextFromHTML, fetchBuilderNewOutlineFromPromptHub, insertTextInHtml} from './aiExtensionUtils'
import {
    aiBuilderInjectionResultConsecutiveErrorMessage,
    aiBuilderInjectionResultConsecutiveErrorType,
    aiBuilderInjectionErrorMessage,
    aiBuilderInjectionErrorType,
    aiContentBadJsonErrorMessage,
    aiContentBadJsonErrorType,
    aiContentUnexpectedError,
    aiContentUnexpectedErrorMessage
} from './constants'
import {injectData, predicateBuilderComp} from './aiBuilderUtils'
import type {ManifestExtensionAPI} from '../builderComponents/manifest'

export const PROPERTY_TYPES = {
    RICH_TEXT: 'richText',
    TEXT: 'text'
} as const

interface PropertyHandler {
    extract(data: any): string
    update(oldValue: any, newText: string): any
}

const PROPERTY_HANDLERS: Record<string, PropertyHandler> = {
    [PROPERTY_TYPES.RICH_TEXT]: {
        extract: (data: string) => extractTextFromHTML(data),
        update: (oldValue: any, newText: string) => ({
            text: insertTextInHtml(oldValue, newText, false)
        })
    },
    [PROPERTY_TYPES.TEXT]: {
        extract: (data: string) => data,
        update: (oldValue: any, newText: string) => newText
    }
}

const BUILDER_PROPERTY_CONFIG = [
    {prop: PROPERTY_TYPES.TEXT},
    {
        prop: PROPERTY_TYPES.RICH_TEXT,
        extract: (data: any) => data.text
    }
]

const extractPropertyData = (prop: string, data: any): string => {
    const handler = PROPERTY_HANDLERS[prop]
    return handler ? handler.extract(data) : data
}

const createUpdateData = (prop: string, oldValue: any, newText: string) => {
    const handler = PROPERTY_HANDLERS[prop]
    const updatedValue = handler ? handler.update(oldValue, newText) : newText

    return {
        data: {
            [prop]: updatedValue
        }
    }
}

interface ValidationResult {
    isValid: boolean
    reason?: string
    value?: string
}

export interface BuilderOutline {
    [section: string]: {[componentName: string]: BuilderComponentData}
}

export interface BuilderComponentData {
    value: string
    metaData: any
    length: number
    prop: string
}

export interface BuilderOutlineWithIdMapAndTargets {
    outline: BuilderOutline
    idMap: IdMap
    targets: string[]
    oldValues: Record<string, any>
}

export type BuilderAiInjectionResult = Record<string, string>

export interface BuilderAiInjectionResultWithIdMapAndOldValues {
    builderAiInjectionResult: BuilderAiInjectionResult
    idMap: IdMap
    oldValues: Record<string, any>
}

const validateBuilderInjectionSuggestion = (
    builderAiInjectionResult: BuilderAiInjectionResult,
    targets: string[]
): ValidationResult => {
    for (const [componentId, data] of Object.entries(builderAiInjectionResult)) {
        if (!targets.includes(componentId))
            return {
                isValid: false,
                reason: 'result contains component that is not specific in targets',
                value: componentId
            }

        if (typeof data !== 'string') {
            return {
                isValid: false,
                reason: 'data generted is not a string',
                value: data
            }
        }
    }

    return {isValid: true}
}

export const applyBuilderInjection = (
    createExtArgs: CreateExtArgs,
    {idMap, builderAiInjectionResult, oldValues}: BuilderAiInjectionResultWithIdMapAndOldValues
) => {
    Object.entries(builderAiInjectionResult).forEach(([componentId, newText]) => {
        const {pointer, prop, path} = idMap[componentId]
        if (!pointer) return
        const updateData = createUpdateData(prop, oldValues[pointer.id][prop], newText)
        injectData(createExtArgs, pointer, path, updateData)
    })
}

export const getPageOutlineAndTargetsForBuilder = (
    createExtArgs: CreateExtArgs,
    componentPointer: Pointer
): BuilderOutlineWithIdMapAndTargets => {
    const {pointers} = createExtArgs
    const {components} = createExtArgs.extensionAPI as StructureExtensionAPI
    const {componentDefinition} = createExtArgs.extensionAPI as ComponentDefinitionExtensionAPI
    const {manifest} = createExtArgs.extensionAPI as ManifestExtensionAPI

    const getContentRecursively = (
        compPointer: Pointer,
        outline: BuilderOutline,
        typeCounter: Record<string, number>,
        idMap: IdMap,
        oldValues: Record<string, any>,
        outlineSectionName: string | undefined,
        pointerPredicate: PointerPredicate,
        selectedComponent: Pointer,
        targets: string[],
        addToTargets: boolean = false
    ): void => {
        if (compPointer.id === selectedComponent.id) addToTargets = true
        const type = components.getComponentType(compPointer)

        if (ignoredTemplateOutlineTypes.has(type)) {
            return
        }

        if (componentDefinition.isContainer(type)) {
            const containerName =
                componentDefinition.isSection(type) &&
                getSectionNameByPointer(createExtArgs.extensionAPI, compPointer, DATA_TYPES.naming)
            const containerOutlineSectionName = containerName
                ? getNextIdForPrefix(containerName, typeCounter)
                : outlineSectionName

            const children = pointers.structure.getChildren(compPointer)
            for (const childPointer of children) {
                getContentRecursively(
                    childPointer,
                    outline,
                    typeCounter,
                    idMap,
                    oldValues,
                    containerOutlineSectionName,
                    pointerPredicate,
                    selectedComponent,
                    targets,
                    addToTargets
                )
            }
        }

        if (pointerPredicate && !pointerPredicate(compPointer)) return

        const propsContext = manifest.getData(compPointer, true, BUILDER_PROPERTY_CONFIG)
        propsContext.forEach(context => {
            const {prop, path, metaData} = context
            let {value: data} = context
            oldValues[compPointer.id] ??= {}
            oldValues[compPointer.id][prop] = data

            data = extractPropertyData(prop, data)

            const mappedId = getNextIdForPrefix('comp', typeCounter)
            idMap[mappedId] = {pointer: compPointer, prop, path}
            const outlineSectionNameOrDefault =
                outlineSectionName ??
                (pointers.structure.isInMasterPage(compPointer) ? HEADER_NAME : unsectionedOutlineSectionName)
            outline[outlineSectionNameOrDefault] ??= {}
            outline[outlineSectionNameOrDefault][mappedId] = {value: data, length: data?.length, metaData, prop}
            if (addToTargets) targets.push(mappedId)
        })
    }

    const outline: BuilderOutline = {}
    const typeCounter: Record<string, number> = {}
    const idMap = {}
    const oldValues = {}

    const predicate = (pointer: Pointer): boolean => {
        const componentType = components.getComponentType(pointer)
        return predicateBuilderComp(createExtArgs.extensionAPI, componentType)
    }

    const pagePointer = pointers.structure.getPageOfComponent(componentPointer)
    const targets: string[] = []
    getContentRecursively(
        pagePointer,
        outline,
        typeCounter,
        idMap,
        oldValues,
        undefined,
        predicate,
        componentPointer,
        targets
    )
    return {outline, idMap, targets, oldValues}
}

export const getComponentInjectionForBuilder = async (
    environmentContext: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    componentPointer: Pointer,
    siteType: string,
    businessName: string,
    siteDescription: string,
    userRequest: string
): Promise<BuilderAiInjectionResultWithIdMapAndOldValues> => {
    const {serverFacade} = environmentContext
    const {coreConfig} = createExtArgs
    const {logger} = coreConfig

    const {outline, idMap, targets, oldValues} = getPageOutlineAndTargetsForBuilder(createExtArgs, componentPointer)
    let builderAiInjectionResult: BuilderAiInjectionResult | null = null

    let apiCallCount = 0
    let lastError: Error | null = null
    const callsMetadata: CompletionCallMetadata[] = []
    let validationResults: ValidationResult

    try {
        while (apiCallCount <= invalidOutlineRetryAmount) {
            const completionResponse = await fetchBuilderNewOutlineFromPromptHub(
                serverFacade,
                userRequest,
                outline,
                targets,
                siteType,
                businessName,
                siteDescription
            )

            const completionText = completionResponse.generatedTexts[0]
            apiCallCount += 1

            try {
                builderAiInjectionResult = JSON.parse(completionText!)
            } catch (e) {
                callsMetadata.push({
                    isBadJson: true
                })
                lastError = new ReportableError({
                    errorType: aiContentBadJsonErrorType,
                    message: aiContentBadJsonErrorMessage,
                    extras: {
                        completionText
                    }
                })
                continue
            }
            validationResults = validateBuilderInjectionSuggestion(builderAiInjectionResult!, targets)
            if (validationResults.isValid) {
                break
            } else if (apiCallCount > 1) {
                reportBIError(
                    logger,
                    aiBuilderInjectionResultConsecutiveErrorType,
                    aiBuilderInjectionResultConsecutiveErrorMessage,
                    builderAiInjectionResult!,
                    validationResults
                )
            } else {
                reportBIError(
                    logger,
                    aiBuilderInjectionErrorType,
                    aiBuilderInjectionErrorMessage,
                    builderAiInjectionResult!,
                    validationResults
                )
            }
        }
        if (!builderAiInjectionResult) {
            throw (
                lastError ??
                new ReportableError({
                    errorType: aiContentUnexpectedError,
                    message: aiContentUnexpectedErrorMessage
                })
            )
        }
        return {builderAiInjectionResult, idMap, oldValues}
    } catch (e: any) {
        // ensure that tokenUsage is always in the error
        const reportedError = getReportableFromError(e, {
            errorType: e.errorType ?? aiContentUnexpectedError,
            tags: {
                aiContentInjection: true
            }
        })
        logger.captureError(reportedError)

        throw reportedError
    }
}
